// src/utils/parser.js — v4.0
// Radio link quality model (NOT WiFi RSSI)

export function parseCSV(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const parts = raw.trim().split(',');
  if (parts.length < 5) return null;
  const parsed = {
    id:        parseInt(parts[0], 10),
    voltage:   parseFloat(parts[1]),
    current:   parseFloat(parts[2]),
    frequency: parseFloat(parts[3]),
    power:     parseFloat(parts[4]),
    timestamp: parts[5] ? parseInt(parts[5], 10) : null,
    // Radio CRC/quality field (optional, sent by sender)
    radioQ:    parts[6] ? parseInt(parts[6], 10) : null,
  };
  if (isNaN(parsed.id) || isNaN(parsed.voltage) ||
      isNaN(parsed.current) || isNaN(parsed.frequency) ||
      isNaN(parsed.power)) return null;
  return parsed;
}

export function validate(p) {
  if (!p) return false;
  return (
    p.voltage   >= 180 && p.voltage   <= 280  &&
    p.current   >= 0   && p.current   <= 30   &&
    p.frequency >= 47  && p.frequency <= 53   &&
    p.power     >= 0   && p.power     <= 8000 &&
    p.id        > 0    && p.id        < 100000
  );
}

/**
 * RADIO LINK QUALITY — 0 to 100
 *
 * Computed from 4 factors that reflect actual radio transmission quality:
 *
 * 1. Valid packet rate (40%) — core indicator: how many packets arrive
 *    intact and pass electrical validation. A corrupted RF frame either
 *    fails CRC on the receiver or produces out-of-range values.
 *
 * 2. Missed packet rate (30%) — gap in sequential IDs means the receiver
 *    never got the frame at all (too much noise / distance / obstacle).
 *
 * 3. Reception gap penalty (20%) — silence longer than expected interval
 *    indicates the link is intermittent or lost entirely.
 *
 * 4. Hardware radio quality field (10%) — optional byte sent by the
 *    XWDT-25 module itself (RSSI or LQI from the 433MHz transceiver chip).
 *    If not present, this factor defaults to neutral (50).
 */
export function computeRadioQuality(packets, lastRxMs, expectedIntervalMs = 2000) {
  if (packets.length === 0) return 0;

  const sample = packets.slice(-30); // rolling window of 30 packets

  // Factor 1 — valid packet rate (packets that passed electrical validation)
  const validRate = sample.filter(p => p.valid && !p.isACK).length /
                    Math.max(1, sample.filter(p => !p.isACK).length);

  // Factor 2 — missed packet detection via ID gaps
  let totalExpected = 0, totalReceived = 0;
  for (let i = 1; i < sample.length; i++) {
    const curr = sample[i - 1];
    const prev = sample[i];
    if (curr.seqId && prev.seqId && curr.seqId > prev.seqId) {
      const gap = curr.seqId - prev.seqId;
      totalExpected += gap;
      totalReceived += 1;
    }
  }
  const receiveRate = totalExpected > 0
    ? Math.min(1, totalReceived / totalExpected)
    : validRate; // fallback if IDs not sequential

  // Factor 3 — silence penalty
  const gapMs     = lastRxMs ? Date.now() - lastRxMs : Infinity;
  const maxGapMs  = expectedIntervalMs * 6; // 6 missed intervals = poor
  const gapScore  = Math.max(0, 1 - gapMs / maxGapMs);

  // Factor 4 — hardware radio quality (0-100 if present, else 50 neutral)
  const hwQ = sample
    .filter(p => p.parsed?.radioQ != null)
    .map(p => p.parsed.radioQ);
  const hwScore = hwQ.length > 0
    ? hwQ.reduce((a, b) => a + b, 0) / hwQ.length / 100
    : 0.5;

  const score = validRate  * 0.40
              + receiveRate * 0.30
              + gapScore    * 0.20
              + hwScore     * 0.10;

  return Math.round(Math.max(0, Math.min(100, score * 100)));
}

/**
 * Radio link quality label + colors + icon character
 */
export function radioQualityMeta(pct) {
  if (pct >= 85) return { label: 'Excellent',   color: '#00e676', dim: '#004d27', icon: '▐▐▐▐▐' };
  if (pct >= 65) return { label: 'Bon',          color: '#69f0ae', dim: '#003d1e', icon: '▐▐▐▐░' };
  if (pct >= 45) return { label: 'Moyen',        color: '#f5a623', dim: '#7a5010', icon: '▐▐▐░░' };
  if (pct >= 25) return { label: 'Faible',       color: '#ff6b35', dim: '#5c2000', icon: '▐▐░░░' };
  if (pct >  0)  return { label: 'Très faible',  color: '#ff3d57', dim: '#5c0011', icon: '▐░░░░' };
  return               { label: 'Aucun signal',  color: '#444441', dim: '#1e1e1c', icon: '░░░░░' };
}

export function countMissedPackets(packets) {
  let missed = 0;
  for (let i = 1; i < packets.length; i++) {
    const curr = packets[i - 1];
    const prev = packets[i];
    if (curr.seqId && prev.seqId && curr.seqId > prev.seqId + 1) {
      missed += curr.seqId - prev.seqId - 1;
    }
  }
  return missed;
}

export function formatTime(date = new Date()) {
  return date.toLocaleTimeString('fr-FR', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
}

// ── Passerelle (secours radio) ───────────────────────────────────
// Format : "<type>,<seq>,<id>;quartier=..;up=..s;lien=..;dj=.."
export function parsePasserelle(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const parts = raw.split(',');
  if (parts.length < 3) return null;
  const type = parts[0].trim();
  if (!/^[A-Z]$/.test(type)) return null;
  const seq = parseInt(parts[1], 10);
  const rest = parts.slice(2).join(',');
  const segs = rest.split(';');
  const id = (segs[0] || '').trim();
  if (!id.startsWith('ADERM')) return null;
  const kv = {};
  for (let i = 1; i < segs.length; i++) {
    const j = segs[i].indexOf('=');
    if (j > 0) kv[segs[i].slice(0, j).trim()] = segs[i].slice(j + 1).trim();
  }
  return { type, seq: isNaN(seq) ? null : seq, id,
           quartier: kv.quartier || null, up: kv.up || null,
           lien: kv.lien || null, dj: kv.dj || null };
}
